import React, { useState } from "react";
import { ProductCardCombined } from "./ProductCardCombined";
import { ProductCard } from "./ProductCard";
import { SubscriptionSelectorValue } from "./SubscriptionSelector";
import { Button } from "@/components/Button";
import { IconButton, Modal } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { productSelect, variantSelect } from "@/store";
import { cn } from "@/helpers/utils";
import { PdpProvider } from "@/features/pdp/PdpContext";
import { AddToCartProductCard } from "@/features/pdp/AddToCartProductCard";
import { ScentSelector } from "@/features/pdp/ScentSelector";
import { ShoppableProductCardProps } from "./ShoppableProductCard";
import { ScentDetails } from "@/store";
import { usePdpFocusedScent } from "@/features/pdp/PdpContext";

export const ShoppableProductCardCombined = ({
  product,
  className,
  ...props
}: ShoppableProductCardProps) => {
  // Map and collect scents
  const allScents = product.collectionProducts?.map((p) => p.scent) ?? [];

  // Filter out undefined values
  const scents = allScents.filter(
    (scent): scent is ScentDetails => scent !== undefined && scent !== null
  );

  return (
    <PdpProvider product={product.product} scents={scents}>
      <ShoppableProductCardInner
        product={product}
        className={className}
        {...props}
      />
    </PdpProvider>
  );
};

export const ShoppableProductCardInner = ({
  product,
  className,
  showRatings,
  ...props
}: ShoppableProductCardProps) => {
  const isPLP = window.location.href.includes("/collections/");
  const isHomePage =
    window.location.pathname === "/" || window.location.pathname === "";
  const showSubSelector = variantSelect.orderableSubscription(product.variant);

  const [showModal, setShowModal] = useState(false);
  const [subToggle, setSubToggle] = useState<SubscriptionSelectorValue>(
    showSubSelector ? "subscription" : "one-time"
  );
  const [toggleScentOpen, setToggleScentOpen] = useState(false);

  const handleModalClose = () => {
    setShowModal(false);
  };

  const scents = product.collectionProducts
    ? product.collectionProducts
        .map((p) => p.scent)
        .filter((scent): scent is ScentDetails => scent != null)
    : [];

  const focusedScent = usePdpFocusedScent();

  const productCardProduct =
    subToggle == "subscription"
      ? {
          ...product,
          price: variantSelect.lowestPrice(product.variant, product.product),
        }
      : product;

  const handleAddToCartSuccess = () => {
    handleModalClose();
  };

  const shopActions = (
    <div className="w-full">
      {scents.length > 0 && (
        <div className="product-card-add-to-cart flex flex-col items-center">
          {focusedScent !== undefined ? (
            <>
              <div className="font-myriad-pro-condensed mb-4 flex w-full items-center justify-between text-xl uppercase">
                <div
                  className="max-w-fit rounded-full px-2 py-1 text-center font-semibold text-black"
                  style={{ backgroundColor: focusedScent?.color }}
                >
                  {focusedScent?.name}
                </div>
                <Button
                  variant="text"
                  onClick={() => {
                    setToggleScentOpen(!toggleScentOpen);
                  }}
                  className="black text-xs font-semibold normal-case tracking-normal underline"
                >
                  {scents.length === 1
                    ? toggleScentOpen
                      ? "Hide details"
                      : "Show details"
                    : toggleScentOpen
                    ? "Hide scent"
                    : "Change scent"}
                </Button>
              </div>
              {toggleScentOpen && (
                <div className="w-full flex-1 items-start px-4">
                  <ScentSelector
                    focusedScent={focusedScent}
                    showBottomPart={false}
                  />
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
      <AddToCartProductCard
        cartSubmitId="AddToCart-product-template"
        onAddToCartSuccess={handleAddToCartSuccess}
      />
    </div>
  );

  const quickAddButton =
    isPLP || isHomePage ? (
      <div className="lg:hidden">
        <div className="relative border-0 border-b border-solid border-primary bg-primary-200 p-2 leading-none">
          <Button
            variant="text"
            size="sm"
            onClick={() => setShowModal(true)}
            className="leading-none after:absolute after:bottom-0 after:left-0 after:top-0 after:h-full after:w-full after:content-['']"
          >
            Quick Add +
          </Button>
        </div>
      </div>
    ) : (
      <div className="inset-0 flex items-end justify-center p-4 lg:hidden">
        <Button
          variant="text"
          size="sm"
          onClick={() => setShowModal(true)}
          className="rounded-lg bg-white px-4 py-2"
        >
          Quick Add +
        </Button>
      </div>
    );

  return (
    <>
      <div
        className={cn("rounded-lg", "scent-consolidation-slider", className)}
      >
        <ProductCardCombined
          {...props}
          productCardValue={productCardProduct}
          hoverable={"expand"}
          bordered={false}
          rounded={false}
          hoverImagesOnly={true}
          showRatings={showRatings}
          quickAddButtons={quickAddButton}
          actions={shopActions}
          cardContentClass="py-2 sm:py-4 sm:px-4"
          showFeaturedText={false}
        />
        <Modal
          className="mx-auto max-w-md overflow-auto sm:mt-5"
          aria-label={`quick add ${productSelect.name(
            product.product
          )} to cart modal`}
          aria-modal={true}
          open={showModal}
          onClose={handleModalClose}
          role="dialog"
        >
          <div className="scent-consolidation-modal">
            <ProductCard
              {...props}
              productCardValue={{
                ...productCardProduct,
                badge: undefined,
                url: undefined,
              }}
              productTitle={
                <h3 data-id="product-card-title" className="title text-lg">
                  {productSelect
                    .name(product.product)
                    .replace(/\b(biobased|power)\b/gi, "")
                    .trim()}
                </h3>
              }
              hoverable={false}
              bordered={false}
              rounded={false}
              actions={shopActions}
              cover={false}
            />
            <IconButton
              className="right-0 top-0 z-50 bg-white"
              size="large"
              onClick={handleModalClose}
              tabIndex={0}
              style={{
                outline: "revert",
                position: "absolute",
                borderRadius: 0,
              }}
              aria-label="close modal"
              color="inherit"
            >
              <ClearIcon className="transition hover:opacity-50" />
            </IconButton>
          </div>
        </Modal>
      </div>
    </>
  );
};
